'use client'

// Solution section — Chapter 02 · The Restoration
// Horizontal core-sample panorama.
// Desktop: pinned section, 6000px track translating horizontally under it,
//          video dwell zone, scroll-tied text reveals, root draw-on,
//          ambient bacteria drift, live progress indicator.
// Mobile:  same JSX, CSS swaps to a vertical stack + simpler animations.

import React, { useRef } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { useSolutionScene } from '@/lib/animations/useSolutionScene'

// ─── Tokens ───────────────────────────────────────────────────────────────────
const CREAM       = '#F4ECD3'
const CREAM_MID   = 'rgba(244,236,211,0.72)'
const CREAM_LOW   = 'rgba(244,236,211,0.48)'
const CREAM_FAINT = 'rgba(244,236,211,0.26)'
const LIME        = '#C8DB3A'
const FONT_BODY   = 'var(--font-body)'
const FONT_SERIF  = 'var(--font-serif)'
const FONT_DISP   = 'var(--font-display)'

const WIDTHS = {
  intro:    880,
  s01:      760,
  s02:      860,
  video:    1080,
  achieves: 1180,
  s03:      800,
  s04:      900,
  close:    720,
} as const
const TRACK_WIDTH = Object.values(WIDTHS).reduce((a, b) => a + b, 0) // 6000

// ─── UI atoms ─────────────────────────────────────────────────────────────────
function Hairline({ width = 40, opacity = 0.28 }: { width?: number; opacity?: number }) {
  return (
    <span
      aria-hidden="true"
      style={{
        flex: `0 0 ${width}px`,
        height: 1,
        background: `rgba(244,236,211,${opacity})`,
        display: 'inline-block',
      }}
    />
  )
}

function PulseDot({ size = 6, color = LIME }: { size?: number; color?: string }) {
  return (
    <span
      aria-hidden="true"
      data-pulse-dot
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: color,
        boxShadow: `0 0 ${size * 2}px ${color}`,
        display: 'inline-block',
        flexShrink: 0,
      }}
    />
  )
}

function StratLabel({
  n,
  name,
  align = 'start',
}: {
  n: string
  name: string
  align?: 'start' | 'end'
}) {
  return (
    <div
      data-anim="label"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        fontSize: 11,
        letterSpacing: '0.34em',
        textTransform: 'uppercase',
        fontWeight: 500,
        color: CREAM_MID,
        marginBottom: 36,
        justifyContent: align === 'end' ? 'flex-end' : 'flex-start',
      }}
    >
      {align === 'end' && <span>{name}</span>}
      <Hairline width={32} />
      <span
        style={{
          fontFamily: FONT_SERIF,
          fontStyle: 'italic',
          fontSize: 22,
          letterSpacing: '-0.02em',
          color: CREAM,
          textTransform: 'none',
          lineHeight: 1,
        }}
      >
        {n}
      </span>
      {align === 'start' && <span>{name}</span>}
    </div>
  )
}

function Datum({ text, align = 'start' }: { text: string; align?: 'start' | 'end' }) {
  return (
    <div
      data-anim="datum"
      style={{
        fontSize: 10.5,
        letterSpacing: '0.34em',
        textTransform: 'uppercase',
        color: CREAM_LOW,
        marginTop: 48,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        justifyContent: align === 'end' ? 'flex-end' : 'flex-start',
      }}
    >
      <PulseDot size={5} color="rgba(244,236,211,0.4)" />
      <span>{text}</span>
    </div>
  )
}

// ─── Decorative: root system SVG ──────────────────────────────────────────────
// Thin scientific line-art; each path uses `data-root-path` for the scrub
// draw-on in useSolutionScene.
function RootSystem({ accent = false }: { accent?: boolean }) {
  const stroke = accent ? 'rgba(232,243,140,0.55)' : 'rgba(244,236,211,0.3)'
  return (
    <svg
      viewBox="0 0 600 420"
      preserveAspectRatio="none"
      style={{ width: '100%', height: '100%', overflow: 'visible' }}
      aria-hidden="true"
    >
      {[
        'M 80 0 C 80 60, 90 110, 110 160 S 140 260, 130 320 L 128 420',
        'M 110 160 C 140 180, 180 185, 210 200',
        'M 130 320 C 170 330, 210 340, 240 360',
        'M 130 220 C 100 240, 70 260, 40 300',
        'M 260 0 C 262 70, 280 140, 310 210 S 330 320, 320 420',
        'M 310 210 C 350 220, 400 220, 440 240',
        'M 320 380 C 280 390, 240 400, 200 410',
        'M 460 0 C 458 80, 470 160, 500 240 S 520 340, 510 420',
        'M 500 240 C 540 250, 570 270, 598 300',
        'M 500 160 C 470 180, 440 180, 410 170',
      ].map((d, i) => (
        <path
          key={i}
          data-root-path
          d={d}
          fill="none"
          stroke={stroke}
          strokeWidth={accent ? 1.2 : 1}
          strokeLinecap="round"
        />
      ))}
    </svg>
  )
}

// ─── Decorative: bacteria particles ──────────────────────────────────────────
// Small lime dots scattered inside a panel. Drift animation is kicked off
// in the hook once mounted.
type Particle = { x: string; y: string; s?: number; o?: number }

function Bacteria({ points }: { points: Particle[] }) {
  return (
    <>
      {points.map((p, i) => (
        <span
          key={i}
          data-bact
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: p.x,
            top: p.y,
            width: p.s ?? 5,
            height: p.s ?? 5,
            borderRadius: '50%',
            background: LIME,
            boxShadow: `0 0 8px rgba(200,219,58,0.7)`,
            opacity: p.o ?? 0.9,
            zIndex: 9,
            pointerEvents: 'none',
          }}
        />
      ))}
    </>
  )
}

// ─── Panel base ──────────────────────────────────────────────────────────────
const panelBase: React.CSSProperties = {
  position: 'relative',
  zIndex: 10,
  flex: '0 0 auto',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: '120px 72px',
  color: CREAM,
  fontFamily: FONT_BODY,
}

// ─── Panels ───────────────────────────────────────────────────────────────────
function SolIntro() {
  const t = useTranslations('solution')
  return (
    <section
      data-panel="intro"
      style={{ ...panelBase, flex: `0 0 ${WIDTHS.intro}px`, padding: '160px 96px 120px' }}
    >
      <div
        data-anim="label"
        style={{ display: 'flex', alignItems: 'baseline', gap: 22, marginBottom: 40 }}
      >
        <span
          style={{
            fontFamily: FONT_SERIF,
            fontStyle: 'italic',
            fontSize: 44,
            lineHeight: 1,
            letterSpacing: '-0.02em',
            color: CREAM,
          }}
        >
          02
        </span>
        <Hairline width={56} />
        <span
          style={{
            fontSize: 11,
            letterSpacing: '0.36em',
            textTransform: 'uppercase',
            color: CREAM_MID,
          }}
        >
          {t('introChapter')} <span style={{ color: LIME }}>{t('introChapterAccent')}</span>
        </span>
      </div>

      <h2
        data-anim="headline"
        style={{
          fontFamily: FONT_DISP,
          fontWeight: 400,
          fontSize: 'clamp(64px, 6vw, 116px)',
          lineHeight: 0.96,
          letterSpacing: '-0.03em',
          color: CREAM,
          margin: '0 0 32px',
          maxWidth: '10ch',
        }}
      >
        {t('introHeadlinePre')}{' '}
        <em
          style={{
            fontFamily: FONT_SERIF,
            fontStyle: 'italic',
            fontWeight: 400,
            color: LIME,
            paddingRight: '0.06em',
          }}
        >
          {t('introHeadlineItalic')}
        </em>
      </h2>

      <p
        data-anim="body"
        style={{
          fontSize: 16,
          lineHeight: 1.6,
          color: CREAM_MID,
          maxWidth: '34ch',
          margin: 0,
          fontWeight: 300,
        }}
      >
        {t('introCopy')}
      </p>
    </section>
  )
}

function Strat01() {
  const t = useTranslations('solution')
  return (
    <section
      data-panel="strat-01"
      style={{ ...panelBase, flex: `0 0 ${WIDTHS.s01}px`, justifyContent: 'flex-start', paddingTop: 220 }}
    >
      <StratLabel n="01" name={t('strat01Label')} />
      <h3
        data-anim="headline"
        style={{
          fontFamily: FONT_DISP,
          fontWeight: 500,
          fontSize: 'clamp(118px, 12vw, 192px)',
          lineHeight: 0.92,
          letterSpacing: '-0.045em',
          color: 'rgba(220, 208, 182, 0.82)',
          margin: 0,
          maxWidth: '10ch',
        }}
      >
        {t('strat01Headline')}
      </h3>
      <Datum text={t('strat01Datum')} />
    </section>
  )
}

function Strat02() {
  const t = useTranslations('solution')
  return (
    <section
      data-panel="strat-02"
      style={{
        ...panelBase,
        flex: `0 0 ${WIDTHS.s02}px`,
        alignItems: 'flex-end',
        textAlign: 'right',
        paddingBottom: 200,
        justifyContent: 'flex-end',
      }}
    >
      <StratLabel n="02" name={t('strat02Label')} align="end" />
      <h3
        data-anim="headline"
        style={{
          fontFamily: FONT_DISP,
          fontWeight: 500,
          fontSize: 'clamp(80px, 8.4vw, 156px)',
          lineHeight: 0.94,
          letterSpacing: '-0.035em',
          color: CREAM,
          margin: 0,
          maxWidth: '12ch',
        }}
      >
        {t('strat02HeadlinePre')}{' '}
        <span
          style={{
            color: LIME,
            fontStyle: 'italic',
            fontFamily: FONT_SERIF,
            fontWeight: 400,
            paddingRight: '0.06em',
          }}
        >
          {t('strat02HeadlineItalic')}
        </span>
      </h3>
      <Datum text={t('strat02Datum')} align="end" />

      <Bacteria
        points={[
          { x: '12%', y: '28%' },
          { x: '28%', y: '64%', s: 4 },
          { x: '68%', y: '22%' },
          { x: '82%', y: '70%', s: 6 },
          { x: '46%', y: '18%', s: 4, o: 0.7 },
          { x: '56%', y: '82%', o: 0.6 },
        ]}
      />
    </section>
  )
}

function SolVideo() {
  const t = useTranslations('solution')
  return (
    <section
      data-panel="video"
      style={{
        position: 'relative',
        zIndex: 10,
        flex: `0 0 ${WIDTHS.video}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '120px 56px',
        color: CREAM,
        fontFamily: FONT_BODY,
      }}
    >
      <div
        data-anim="video-frame"
        role="img"
        aria-label={t('videoAlt')}
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '16 / 9',
          background:
            'radial-gradient(120% 70% at 50% 50%, #3C2A10 0%, #1B0E04 70%, #0B0502 100%)',
          border: '1px solid rgba(244,236,211,0.16)',
          borderRadius: 14,
          overflow: 'hidden',
          boxShadow:
            '0 40px 100px -24px rgba(0,0,0,0.85), 0 0 0 1px rgba(200,219,58,0.06)',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(60% 80% at 30% 30%, rgba(139,90,40,0.35) 0%, rgba(139,90,40,0) 60%), radial-gradient(40% 60% at 75% 70%, rgba(200,219,58,0.18) 0%, rgba(200,219,58,0) 70%), repeating-linear-gradient(48deg, rgba(0,0,0,0.16) 0 3px, rgba(0,0,0,0) 3px 9px)',
            mixBlendMode: 'multiply',
          }}
        />

        <div
          style={{
            position: 'absolute',
            top: 20,
            left: 22,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 10.5,
            letterSpacing: '0.34em',
            textTransform: 'uppercase',
            color: CREAM_MID,
            zIndex: 2,
          }}
        >
          <span
            aria-hidden="true"
            data-rec-dot
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: LIME,
              boxShadow: `0 0 12px ${LIME}`,
              display: 'inline-block',
            }}
          />
          <span>{t('videoRec')}</span>
        </div>

        <div
          data-anim="video-caption"
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            zIndex: 2,
          }}
        >
          <div
            style={{
              fontSize: 11.5,
              letterSpacing: '0.4em',
              textTransform: 'uppercase',
              color: CREAM_LOW,
              marginBottom: 18,
              fontWeight: 500,
            }}
          >
            {t('videoLabel')}
          </div>
          <div
            style={{
              fontFamily: FONT_SERIF,
              fontStyle: 'italic',
              fontSize: 'clamp(42px, 4vw, 68px)',
              lineHeight: 1,
              letterSpacing: '-0.02em',
              color: CREAM,
            }}
          >
            {t('videoCaption')}
          </div>
        </div>
      </div>
    </section>
  )
}

function SolAchieves() {
  const t = useTranslations('solution')
  const items = [1, 2, 3, 4] as const
  return (
    <section
      data-panel="achieves"
      style={{
        ...panelBase,
        flex: `0 0 ${WIDTHS.achieves}px`,
        justifyContent: 'center',
        padding: '120px 80px',
      }}
    >
      <div
        data-anim="card"
        style={{
          background: 'rgba(15,12,6,0.42)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          border: '1px solid rgba(244,236,211,0.16)',
          borderRadius: 20,
          padding: '52px 56px',
          boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
        }}
      >
        <div
          data-anim="label"
          style={{
            fontSize: 11,
            letterSpacing: '0.36em',
            textTransform: 'uppercase',
            color: LIME,
            fontWeight: 700,
            marginBottom: 22,
          }}
        >
          {t('achievesEyebrow')}
        </div>
        <h3
          data-anim="headline"
          style={{
            fontFamily: FONT_DISP,
            fontWeight: 400,
            fontSize: 'clamp(40px, 4vw, 64px)',
            lineHeight: 1.04,
            letterSpacing: '-0.02em',
            color: CREAM,
            margin: '0 0 36px',
            maxWidth: '20ch',
          }}
        >
          {t('achievesTitle')}
        </h3>
        <ul
          data-anim="list"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '28px 56px',
            listStyle: 'none',
            margin: 0,
            padding: 0,
          }}
        >
          {items.map((n) => (
            <li key={n}>
              <div
                style={{
                  fontFamily: FONT_BODY,
                  fontSize: 11.5,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: LIME,
                  marginBottom: 10,
                  fontWeight: 700,
                }}
              >
                {t(`achievesItem${n}Title`)}
              </div>
              <div
                style={{
                  fontFamily: FONT_BODY,
                  fontSize: 14.5,
                  lineHeight: 1.55,
                  color: CREAM_MID,
                  fontWeight: 300,
                }}
              >
                {t(`achievesItem${n}Body`)}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}

function Strat03() {
  const t = useTranslations('solution')
  return (
    <section
      data-panel="strat-03"
      style={{ ...panelBase, flex: `0 0 ${WIDTHS.s03}px`, justifyContent: 'center' }}
    >
      {/* Root system behind the type */}
      <div
        style={{
          position: 'absolute',
          top: '15%',
          right: '-4%',
          width: 520,
          height: 520,
          zIndex: 3,
          opacity: 0.8,
          pointerEvents: 'none',
        }}
      >
        <RootSystem />
      </div>

      <StratLabel n="03" name={t('strat03Label')} />
      <h3
        data-anim="headline"
        style={{
          fontFamily: FONT_DISP,
          fontWeight: 500,
          fontSize: 'clamp(96px, 10vw, 184px)',
          lineHeight: 0.92,
          letterSpacing: '-0.045em',
          color: '#CFD37D',
          margin: 0,
          maxWidth: '10ch',
        }}
      >
        {t('strat03Headline')}
      </h3>
      <Datum text={t('strat03Datum')} />

      <Bacteria
        points={[
          { x: '18%', y: '56%' },
          { x: '38%', y: '28%', s: 4 },
          { x: '54%', y: '74%', s: 5 },
          { x: '30%', y: '84%', s: 4, o: 0.6 },
          { x: '72%', y: '46%', s: 5, o: 0.7 },
        ]}
      />
    </section>
  )
}

function Strat04() {
  const t = useTranslations('solution')
  return (
    <section
      data-panel="strat-04"
      style={{ ...panelBase, flex: `0 0 ${WIDTHS.s04}px`, justifyContent: 'flex-start', paddingTop: 200 }}
    >
      {/* Denser, accent-colored root system */}
      <div
        style={{
          position: 'absolute',
          top: '8%',
          left: '-4%',
          width: 580,
          height: 620,
          zIndex: 3,
          opacity: 0.8,
          pointerEvents: 'none',
        }}
      >
        <RootSystem accent />
      </div>

      <StratLabel n="04" name={t('strat04Label')} />
      <h3
        data-anim="headline"
        style={{
          fontFamily: FONT_DISP,
          fontWeight: 500,
          fontSize: 'clamp(88px, 9vw, 168px)',
          lineHeight: 0.94,
          letterSpacing: '-0.035em',
          color: '#F1F3D4',
          margin: 0,
          maxWidth: '14ch',
        }}
      >
        {t('strat04HeadlinePre')}{' '}
        <em
          style={{
            fontFamily: FONT_SERIF,
            fontStyle: 'italic',
            fontWeight: 400,
            paddingRight: '0.06em',
          }}
        >
          {t('strat04HeadlineItalic')}
        </em>{' '}
        {t('strat04HeadlinePost')}
      </h3>
      <Datum text={t('strat04Datum')} />

      <Bacteria
        points={[
          { x: '62%', y: '18%' },
          { x: '78%', y: '36%', s: 5 },
          { x: '52%', y: '58%', s: 6 },
          { x: '40%', y: '78%', s: 4 },
          { x: '86%', y: '68%', s: 4, o: 0.7 },
          { x: '70%', y: '86%', s: 5, o: 0.8 },
          { x: '28%', y: '24%', s: 4, o: 0.6 },
        ]}
      />
    </section>
  )
}

function SolClose() {
  // The Close panel occupies only the middle 62% of the section — the
  // top (black atmosphere) and bottom (subsoil) stay in place around it.
  // Uses brand olive-500 (#5B7005) as the solid field; cream text for
  // contrast; lime-400 accents the italic "Restored." for a final pop.
  const OLIVE_FIELD = '#5B7005'
  const t = useTranslations('solution')
  const locale = useLocale()
  const techHref = locale === 'es' ? '/tecnologia' : '/tech'

  return (
    <section
      data-panel="close"
      style={{
        position: 'relative',
        flex: `0 0 ${WIDTHS.close}px`,
        padding: 0,
        // Transparent — the olive fill lives on an inner strip so it
        // matches the core cylinder's vertical extent (22% → 84%) and
        // leaves the black top + bottom bands untouched.
        background: 'transparent',
      }}
    >
      {/* Olive strip — same vertical extent as the core cylinder */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: '22%',
          height: '62%',
          background: `linear-gradient(to right, rgba(91,112,5,0) 0%, ${OLIVE_FIELD} 28%, ${OLIVE_FIELD} 100%)`,
          boxShadow:
            'inset 0 14px 32px -8px rgba(0,0,0,0.45), inset 0 -18px 40px -8px rgba(0,0,0,0.55)',
          zIndex: 4,
        }}
      />

      {/* Content sits above the olive strip */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: '22%',
          height: '62%',
          zIndex: 12,
          padding: '0 96px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          color: CREAM,
          fontFamily: FONT_BODY,
        }}
      >
        <div
          data-anim="label"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            fontSize: 11,
            letterSpacing: '0.36em',
            textTransform: 'uppercase',
            color: 'rgba(244,236,211,0.7)',
            marginBottom: 36,
            fontWeight: 600,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 40,
              height: 1,
              background: 'rgba(244,236,211,0.38)',
              display: 'inline-block',
            }}
          />
          <span>{t('closeLabel')}</span>
        </div>
        <h3
          data-anim="headline"
          style={{
            fontFamily: FONT_DISP,
            fontWeight: 400,
            fontSize: 'clamp(80px, 7vw, 128px)',
            lineHeight: 0.96,
            letterSpacing: '-0.025em',
            color: CREAM,
            margin: '0 0 28px',
          }}
        >
          <em
            style={{
              fontFamily: FONT_SERIF,
              fontStyle: 'italic',
              fontWeight: 400,
              color: '#E7EAC0', // olive-100, soft cream-green, reads as "living"
              paddingRight: '0.06em',
            }}
          >
            {t('closeHeadlineItalic')}
          </em>
        </h3>
        <a
          data-anim="cta"
          href={techHref}
          className="sol-cta"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 12,
            letterSpacing: '0.36em',
            textTransform: 'uppercase',
            fontWeight: 600,
            color: CREAM,
            textDecoration: 'none',
            alignSelf: 'flex-start',
            borderBottom: '1px solid rgba(244,236,211,0.42)',
            paddingBottom: 6,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {t('closeCta')}
          <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1 }}>→</span>
        </a>
      </div>
    </section>
  )
}

// ─── Section root ─────────────────────────────────────────────────────────────
export default function Solution() {
  const rootRef = useRef<HTMLElement>(null)
  useSolutionScene(rootRef)

  return (
    <section
      ref={rootRef}
      id="solution"
      className="sol-section"
      style={{
        position: 'relative',
        height: '100vh',
        overflow: 'hidden',
        background: '#0E0804',
        color: CREAM,
        isolation: 'isolate',
      }}
    >
      {/* Above cylinder */}
      <div
        aria-hidden="true"
        className="sol-above"
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 0,
          height: '22%',
          zIndex: 0,
          background:
            'linear-gradient(to right, #0B0604 0%, #110A04 18%, #160E05 36%, #1A1204 60%, #231808 82%, #3A2C0E 100%)',
        }}
      />

      {/* Core cylinder gradient — ends in deep olive, not cream */}
      <div
        aria-hidden="true"
        className="sol-core"
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: '22%',
          height: '62%',
          zIndex: 1,
          overflow: 'hidden',
          background:
            'linear-gradient(to right, #6B6358 0%, #7A6448 14%, #83542B 26%, #7A4A1E 40%, #5C4217 52%, #5B5A1D 66%, #5B7005 82%, #5B7005 100%)',
          boxShadow:
            '0 -1px 0 rgba(244,236,211,0.08), 0 1px 0 rgba(244,236,211,0.08), inset 0 14px 32px -8px rgba(0,0,0,0.55), inset 0 -18px 40px -8px rgba(0,0,0,0.6)',
        }}
      />

      {/* Lime intervention hairline */}
      <div
        aria-hidden="true"
        className="sol-lime-line"
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: '22%',
          height: 1,
          zIndex: 2,
          background:
            'linear-gradient(to right, rgba(200,219,58,0) 0%, rgba(200,219,58,0) 8%, rgba(200,219,58,0.35) 22%, rgba(200,219,58,0.7) 50%, rgba(200,219,58,0.35) 82%, rgba(200,219,58,0) 100%)',
          boxShadow: '0 0 22px rgba(200,219,58,0.4)',
        }}
      />

      {/* Below cylinder */}
      <div
        aria-hidden="true"
        className="sol-below"
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: '16%',
          zIndex: 0,
          background:
            'linear-gradient(to right, #060301 0%, #0B0603 30%, #140C04 60%, #221604 100%)',
        }}
      />

      {/* Top chrome removed — the "02 · Chapter Two" label and
          "VLC-07 · 2026" marker were colliding with the global nav
          when the section pinned under the fixed header. */}

      {/* Horizontal track — all panels in a row */}
      <div
        data-sol-track
        className="sol-track"
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: TRACK_WIDTH,
          height: '100%',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'stretch',
          zIndex: 5,
          willChange: 'transform',
        }}
      >
        <SolIntro />
        <Strat01 />
        <Strat02 />
        <SolVideo />
        <SolAchieves />
        <Strat03 />
        <Strat04 />
        <SolClose />
      </div>

      {/* Horizontal progress indicator */}
      <div
        aria-hidden="true"
        className="sol-progress"
        style={{
          position: 'absolute',
          bottom: 36,
          left: 48,
          right: 48,
          zIndex: 70,
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 10,
            letterSpacing: '0.36em',
            textTransform: 'uppercase',
            fontWeight: 500,
            color: CREAM_FAINT,
            marginBottom: 14,
          }}
        >
          <span>Start</span>
          <span>01</span>
          <span>02</span>
          <span style={{ color: LIME }}>10 wks</span>
          <span>03</span>
          <span>04</span>
          <span>Closed</span>
        </div>
        <div
          style={{
            position: 'relative',
            height: 1,
            background: 'rgba(244,236,211,0.16)',
          }}
        >
          <div
            data-progress-fill
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '0%',
              background: LIME,
              boxShadow: `0 0 10px ${LIME}`,
            }}
          />
          <div
            data-progress-dot
            style={{
              position: 'absolute',
              top: '50%',
              left: '0%',
              transform: 'translate(-50%, -50%)',
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: LIME,
              boxShadow: `0 0 14px ${LIME}`,
            }}
          />
        </div>
      </div>

      {/* CTA hover + mobile stacked-panel CSS */}
      <style>{`
        .sol-cta {
          transition: color 260ms ease, border-color 260ms ease;
        }
        .sol-cta:hover {
          color: #E7EAC0;
          border-color: rgba(231,234,192,0.8);
        }

        @media (max-width: 767px), (hover: none) and (pointer: coarse) {
          .sol-section {
            height: auto !important;
            overflow: visible !important;
          }
          .sol-above,
          .sol-below,
          .sol-lime-line {
            display: none !important;
          }
          .sol-core {
            position: absolute !important;
            left: 0 !important;
            right: 0 !important;
            top: 0 !important;
            height: 100% !important;
            background: linear-gradient(
              to bottom,
              #6B6358 0%,
              #7A6448 15%,
              #83542B 30%,
              #5B5A1D 55%,
              #5B7005 75%,
              #8F9A1B 90%,
              #E8DAB6 100%
            ) !important;
            box-shadow: none !important;
          }
          .sol-track {
            position: relative !important;
            width: 100% !important;
            height: auto !important;
            flex-direction: column !important;
            left: auto !important;
            top: auto !important;
            transform: none !important;
            will-change: auto !important;
          }
          .sol-track > section {
            flex: 0 0 auto !important;
            width: 100% !important;
            min-height: auto !important;
            padding: 72px 24px !important;
            justify-content: center !important;
            align-items: flex-start !important;
            text-align: left !important;
          }
          .sol-track > section[data-panel="strat-02"] {
            align-items: flex-start !important;
            text-align: left !important;
          }
          .sol-track > section[data-panel="strat-02"] [data-anim="label"] {
            justify-content: flex-start !important;
          }
          .sol-track > section[data-panel="video"] {
            padding: 56px 20px !important;
          }
          /* Hide the REC · LOOP corner badge on mobile — it overlaps the
             centered video caption and is decorative chrome only. */
          .sol-track > section[data-panel="video"] [data-rec-dot] {
            display: none;
          }
          .sol-track > section[data-panel="video"] [data-rec-dot] ~ span,
          .sol-track > section[data-panel="video"] > div > div:nth-of-type(2) {
            display: none !important;
          }
          /* Shrink the centered caption so the label + italic both fit
             cleanly inside the 16:9 video box without overflowing. */
          .sol-track > section[data-panel="video"] [data-anim="video-caption"] > div:first-child {
            font-size: 10px !important;
            letter-spacing: 0.32em !important;
            margin-bottom: 12px !important;
          }
          .sol-track > section[data-panel="video"] [data-anim="video-caption"] > div:last-child {
            font-size: clamp(24px, 6.4vw, 36px) !important;
          }
          .sol-track > section h3,
          .sol-track > section h2 {
            font-size: clamp(44px, 12vw, 80px) !important;
          }

          /* Achievements card: 2x2 grid → single stacked column,
             card padding tightens. */
          .sol-track > section[data-panel="achieves"] {
            padding: 64px 20px !important;
          }
          .sol-track > section[data-panel="achieves"] > div {
            padding: 32px 24px !important;
            border-radius: 16px !important;
          }
          .sol-track > section[data-panel="achieves"] h3 {
            font-size: clamp(28px, 7.4vw, 40px) !important;
            margin-bottom: 24px !important;
          }
          .sol-track > section[data-panel="achieves"] ul {
            grid-template-columns: 1fr !important;
            gap: 22px !important;
          }
          .sol-track > section[data-panel="achieves"] ul li > div:first-child {
            font-size: 11px !important;
          }
          .sol-track > section[data-panel="achieves"] ul li > div:last-child {
            font-size: 14px !important;
          }

          /* Strat 04 multi-part headline + Strat 03 single line stay readable */
          .sol-track > section[data-panel="strat-04"] h3 {
            max-width: none !important;
          }

          /* Hide oversized decorative root SVGs on mobile — they overflow
             the panel and clutter the layout. The Bacteria particles can
             stay; they're small and ambient. */
          .sol-track > section[data-panel="strat-03"] > div:first-child,
          .sol-track > section[data-panel="strat-04"] > div:first-child {
            display: none !important;
          }

          /* Close panel: olive strip becomes a full-bleed background,
             content flows naturally inside it (instead of two stacked
             absolute layers). */
          .sol-track > section[data-panel="close"] {
            padding: 0 !important;
          }
          .sol-track > section[data-panel="close"] > div:nth-child(1) {
            position: absolute !important;
            inset: 0 !important;
            top: 0 !important;
            height: 100% !important;
            background: linear-gradient(180deg,
              rgba(91,112,5,0) 0%,
              #5B7005 12%,
              #5B7005 100%) !important;
          }
          .sol-track > section[data-panel="close"] > div:nth-child(2) {
            position: relative !important;
            inset: auto !important;
            top: auto !important;
            height: auto !important;
            min-height: 60vh;
            padding: 96px 28px 72px !important;
            justify-content: center !important;
          }

          .sol-progress {
            display: none !important;
          }
        }
      `}</style>
    </section>
  )
}
